import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import InitiativeCard from '../components/initiatives/InitiativeCard';
import { Container, Section, SectionLabel, Eyebrow, PageLoading } from '../components/ui/Section';
import { useSiteContent } from '../hooks/useSiteContent';
import { useInitiativeData } from '../hooks/useInitiativeData';
import { Initiative } from '../types';

gsap.registerPlugin(ScrollTrigger);

const PageHeader = styled.section`
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.sectionPadding.md} 0 ${props => props.theme.sectionPadding.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderContent = styled.div`
  max-width: 800px;
`;

const PageTitle = styled(motion.h1)`
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const PageSubtitle = styled(motion.p)`
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.textMuted};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  max-width: 56ch;
`;

const InitiativesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing[12]} 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.textMuted};
  max-width: 56ch;

  h3 {
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    margin-bottom: ${props => props.theme.spacing[3]};
    color: ${props => props.theme.colors.ink};
  }

  p {
    font-size: ${props => props.theme.typography.fontSize.base};
    line-height: ${props => props.theme.typography.lineHeight.relaxed};
  }
`;

// Wrapper component to use the hook for each initiative
const InitiativeCardWrapper: React.FC<{ initiative: Initiative }> = ({ initiative }) => {
  const initiativeWithData = useInitiativeData(initiative);

  if (!initiativeWithData) {
    return null;
  }

  return <InitiativeCard initiative={initiativeWithData} />;
};

const Initiatives: React.FC = () => {
  const { siteContent, isLoading } = useSiteContent();
  const initiatives = siteContent?.initiatives;

  const currentCardsRef = useRef<HTMLDivElement>(null);
  const pastCardsRef = useRef<HTMLDivElement>(null);
  const upcomingCardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || !initiatives) return;

    // Animate current initiatives cards with stagger
    if (currentCardsRef.current && initiatives.current.length > 0) {
      const cards = currentCardsRef.current.children;
      gsap.fromTo(cards,
        { opacity: 0.5, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: currentCardsRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Animate past initiatives cards with stagger
    if (pastCardsRef.current && initiatives.past.length > 0) {
      const cards = pastCardsRef.current.children;
      gsap.fromTo(cards,
        { opacity: 0.5, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: pastCardsRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    // Animate upcoming initiatives cards with stagger
    if (upcomingCardsRef.current && initiatives.upcoming.length > 0) {
      const cards = upcomingCardsRef.current.children;
      gsap.fromTo(cards,
        { opacity: 0.5, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: upcomingCardsRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none'
          }
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isLoading, initiatives]);

  if (isLoading || !initiatives) {
    return <PageLoading />;
  }

  return (
    <div>
      <PageHeader>
        <Container>
          <HeaderContent>
            <Eyebrow>Our work</Eyebrow>
            <PageTitle
              initial={{ opacity: 0.5, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              Initiatives
            </PageTitle>
            <PageSubtitle
              initial={{ opacity: 0.5, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
            >
              Youth-led campaigns creating meaningful change in healthcare, education, and community wellbeing.
            </PageSubtitle>
          </HeaderContent>
        </Container>
      </PageHeader>

      {/* Current Initiatives */}
      {initiatives.current.length > 0 ? (
        <Section $size="lg">
          <Container>
            <SectionLabel>
              {initiatives.current.length === 1 ? 'Current initiative' : 'Current initiatives'}
            </SectionLabel>
            <InitiativesGrid ref={currentCardsRef}>
              {initiatives.current.map((initiative) => (
                <InitiativeCardWrapper key={initiative.id} initiative={initiative} />
              ))}
            </InitiativesGrid>
          </Container>
        </Section>
      ) : (
        <Section $size="lg">
          <Container>
            <SectionLabel>Current initiatives</SectionLabel>
            <EmptyState>
              <h3>No active campaigns</h3>
              <p>We're currently planning our next initiative. Check back soon or view our past work below.</p>
            </EmptyState>
          </Container>
        </Section>
      )}

      {/* Past Initiatives */}
      {initiatives.past.length > 0 ? (
        <Section $surface="muted" $size="lg">
          <Container>
            <SectionLabel align="left">Past initiatives</SectionLabel>
            <InitiativesGrid ref={pastCardsRef}>
              {initiatives.past.map((initiative) => (
                <InitiativeCardWrapper key={initiative.id} initiative={initiative} />
              ))}
            </InitiativesGrid>
          </Container>
        </Section>
      ) : (
        <Section $surface="muted" $size="lg">
          <Container>
            <SectionLabel align="left">Past initiatives</SectionLabel>
            <EmptyState>
              <h3>Past initiatives</h3>
              <p>As we grow, our completed campaigns will be showcased here.</p>
            </EmptyState>
          </Container>
        </Section>
      )}

      {/* Upcoming Initiatives */}
      {initiatives.upcoming.length > 0 && (
        <Section $size="lg">
          <Container>
            <SectionLabel>Upcoming initiatives</SectionLabel>
            <InitiativesGrid ref={upcomingCardsRef}>
              {initiatives.upcoming.map((initiative) => (
                <InitiativeCardWrapper key={initiative.id} initiative={initiative} />
              ))}
            </InitiativesGrid>
          </Container>
        </Section>
      )}
    </div>
  );
};

export default Initiatives;
